import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** Create has its own backoffice-api endpoint: through the proxy the key would be the operator's. */
const CREATE_KEY_PATH = "/access-keys";
const TENANT_KEYS_PATH = "/proxy/access-keys/api/v1/access-keys";
const EMPLOYEE_SEARCH_PATH =
  "/proxy/employees/api/v1/employees/minimized/search";

/** access-keys' page size cap. */
const KEYS_PAGE_SIZE = 100;

export type AccessKeyRole = "Read" | "Read + Write";

export const ACCESS_KEY_EXPIRATIONS = [
  "30 days",
  "60 days",
  "90 days",
  "6 months",
  "1 year",
] as const;
export type AccessKeyExpiration = (typeof ACCESS_KEY_EXPIRATIONS)[number];

/** Mirrors access-keys ExternalAccessKeyResponse. Never carries the secret. */
export interface AccessKey {
  access_key_id: string;
  name: string;
  description: string | null;
  roles: AccessKeyRole[];
  scope_type: "platform" | "external";
  is_active: boolean;
  expire_time: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
}

/** Mirrors backoffice-api TenantAccessKeyCreated. `access_key` is the secret. */
interface AccessKeyCreated {
  access_key_id: string;
  access_key: string;
}

interface CreateAccessKeyInput {
  tenant_id: string;
  employee_id: string;
  name: string;
  description?: string;
  roles: AccessKeyRole[];
  expiration: AccessKeyExpiration;
}

/** Mirrors employees MinimizedEmployee. */
export interface MinimizedEmployee {
  id: string;
  work_email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
}

interface PaginatedResponse<T> {
  total_count: number;
  items: T[];
}

const accessKeysQueryKey = ["access-keys"] as const;

export function employeeLabel(employee: MinimizedEmployee): string {
  const fullName = [employee.first_name, employee.last_name]
    .filter(Boolean)
    .join(" ");
  return employee.display_name || fullName || employee.work_email;
}

export function useEmployeeSearch(
  tenantId: string,
  query: string,
  enabled: boolean,
) {
  const q = query.trim();
  return useQuery({
    queryKey: ["employees", "search", tenantId, q],
    queryFn: async () => {
      const { data } = await backofficeClient.get<
        PaginatedResponse<MinimizedEmployee>
      >(EMPLOYEE_SEARCH_PATH, {
        params: { tenant_id: tenantId, q: q === "" ? undefined : q, limit: 20 },
      });
      return data.items;
    },
    enabled: enabled && Boolean(tenantId),
    staleTime: 60_000,
  });
}

export function useTenantAccessKeys(tenantId: string) {
  return useQuery({
    queryKey: [...accessKeysQueryKey, tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<PaginatedResponse<AccessKey>>(
        TENANT_KEYS_PATH,
        { params: { tenant_id: tenantId, page: 0, page_size: KEYS_PAGE_SIZE } },
      );
      return data;
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateAccessKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAccessKeyInput) => {
      const { data } = await backofficeClient.post<AccessKeyCreated>(
        CREATE_KEY_PATH,
        input,
        { routeTenantId: input.tenant_id },
      );
      return data;
    },
    // The result carries the secret; keep nothing once it settles
    gcTime: 0,
    // Also after a failure: access-keys may have created the key regardless
    onSettled: (_created, _error, input) => {
      void queryClient.invalidateQueries({
        queryKey: [...accessKeysQueryKey, input.tenant_id],
      });
    },
  });
}

export function useDeleteAccessKey(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accessKeyId: string) => {
      await backofficeClient.delete(`${TENANT_KEYS_PATH}/${accessKeyId}`, {
        params: { tenant_id: tenantId },
      });
    },
    // Returned, so the row stays busy until the refetch removes it
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...accessKeysQueryKey, tenantId],
      }),
  });
}

/** backoffice-api answers with a plain `detail`; FastAPI validation errors carry a list of them. */
export function accessKeyErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!isAxiosError<{ detail?: unknown }>(error)) return fallback;
  const detail: unknown = error.response?.data.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item: unknown) =>
      typeof item === "object" &&
      item !== null &&
      "msg" in item &&
      typeof item.msg === "string"
        ? [item.msg]
        : [],
    );
    if (messages.length > 0) return messages.join("; ");
  }
  return fallback;
}
